/**
 * I33 — a SHARE-LINK visitor's office-document socket (`/ws/office`): the same session
 * signals a native editor receives on its drive socket (a Discard, a conflict, the unsaved
 * flag, Keep mine's confirmation), for an account-less (or other-org) visitor.
 *
 * The drive's socket is a tenant socket; a share visitor must never hold one of the owner's
 * tenant. This one carries only office-document frames: the server admits each document by
 * a WOPI token of THIS share (re-judged live). It drives `shareOfficeDocChannel` exactly as
 * useRealtime drives the session channel — same registry, same resync on a reconnect — and
 * reuses the same connect machinery (@papan/reconnect backoff + handshake timeout, the
 * derived WS origin, a single-use token minted over the authenticated REST path — here the
 * share session, carried by api-client's X-Share-Session header).
 */
import { useEffect } from 'react'
import { COLLAB_SESSION_REVOKED_EVENT } from '@papan/types'
import { request, deriveWsOrigin, currentSessionSignal } from '@papan/api-client'
import { armConnectTimeout, createReconnectController } from '@papan/reconnect'
import { shareOfficeDocChannel } from './office-docs.js'

/** Hold the share office socket while `enabled` (an office editor is open in the share view). `shareSessionId`: the
 *  share session it serves (the chrome's active sid) — #1316 review round 1: a new share session (an identity switch,
 *  a replaced session: the view stays, the end closed the old socket) opens a new socket, minted for it.
 *  `reopenKey` (review round 2): moved by the view at an end that KEEPS its share session (a sign-out in another tab
 *  for an unbound visit, a revoke the server did not confirm) — the end closed the socket and the sid stays, so this
 *  opens it again, as the session now here. Never moved at an end that moves the share session: the sid lags until
 *  the new one lands, and the new sid reopens it then. */
export function useShareOfficeSocket(enabled: boolean, shareSessionId?: string | null, reopenKey = 0): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    let alive = true
    let ws: WebSocket | null = null
    let ping: ReturnType<typeof setInterval> | null = null
    const reconnect = createReconnectController({ baseMs: 1_000, ceilingMs: 30_000, stableAfterMs: 10_000, maxAttempts: 60 })

    const connect = async () => {
      if (!alive) return
      let token: string | undefined
      try {
        token = (await request<{ token?: string }>('/api/realtime/office-share-ws-token', { method: 'POST' }))?.token
      } catch { /* mint failed — back off */ }
      if (!alive) return
      if (!token) { reconnect.schedule(() => { void connect() }); return }
      const base = ((import.meta.env.VITE_WS_URL as string | undefined) || '') || deriveWsOrigin(window.location.hostname)
        || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
      const sock = new WebSocket(`${base.replace(/\/$/, '')}/ws/office?${new URLSearchParams({ ws_token: token })}`)
      ws = sock
      const clearConnectTimeout = armConnectTimeout(sock, {})
      sock.onopen = () => {
        clearConnectTimeout()
        reconnect.markConnected()
        ping = setInterval(() => { if (sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify({ type: 'ping' })) }, 30_000)
        shareOfficeDocChannel.attachSender((m) => { if (sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify(m)) })
      }
      sock.onmessage = (e) => {
        let msg: { type?: string } | null = null
        try { msg = JSON.parse(String(e.data)) } catch { return }
        if (msg?.type === 'oo-doc-subscribed') shareOfficeDocChannel.onSubscribed(msg as never)
        else if (msg?.type === 'oo-doc') shareOfficeDocChannel.onEvent(msg as never)
      }
      sock.onclose = (event) => {
        clearConnectTimeout()
        // #809: closed because the share session was LOGGED OUT (a "Log out" in another tab
        // ends the whole visit) — the share view asks the server and, if so, leaves for the
        // link's front door, as it does for a live text editor (useYjs).
        if (event.reason === 'session_revoked') window.dispatchEvent(new CustomEvent(COLLAB_SESSION_REVOKED_EVENT))
        if (ping) { clearInterval(ping); ping = null }
        shareOfficeDocChannel.attachSender(null)
        reconnect.onDropped()
        if (alive) reconnect.schedule(() => { void connect() })
      }
    }

    // #1316: the socket belongs to the session that opened it — when that session ends, do what the cleanup does BEFORE
    // closing (else the close schedules a reconnect, which would open a socket as the NEXT session). @papan/realtime
    // doesn't depend on core-ui, so the end is read off the session's own signal (every end aborts it).
    const session = currentSessionSignal()
    const stop = () => {
      alive = false
      reconnect.cancel()
      if (ping) { clearInterval(ping); ping = null }
      if (ws) { ws.onclose = null; ws.close(); ws = null }
      shareOfficeDocChannel.attachSender(null)
    }
    session?.addEventListener('abort', stop, { once: true })

    void connect()
    return () => {
      session?.removeEventListener('abort', stop)
      alive = false
      reconnect.cancel()
      if (ping) clearInterval(ping)
      if (ws) { ws.onclose = null; ws.close() }
      shareOfficeDocChannel.attachSender(null)
    }
  }, [enabled, shareSessionId, reopenKey])
}
